import React, { useImperativeHandle, useRef, useMemo, forwardRef } from "react";
import {
	IGallery,
	INVITATION_OCCASION,
	INVITATION_TYPES,
	Invitations,
	LangType
} from "../../../../store/services/invitations/dto/invitations.dto";
import { getFieldMap } from "../../../../utils/inputs";
import { LANG_AR, LANG_EN, RTL } from "../constants";
import langEN from "../lang.EN.json";
import langAR from "../lang.AR.json";
import { TemplateColorObject } from "../../../../InvitationColorMatrix";
import { getWeddingDate } from "../../../../utils/formatDate";
import SaveTheDateBtn from "../Components/SaveTheDateBtn";
import AddIcon from "@mui/icons-material/Add";
import InvitationGallery from "../../../InvitationGallery";
import Comments from "../Components/Comments";
import PartnersSocialList from "../Components/PartnersSocialList";
import CountDown from "../CountDown";
import MapButton from "../Components/MapButton";
import MapComponent from "../Components/MapComponent/MapComponent";
import Footer from "../Components/Footer";
import { TemplateWrapper } from "./styles";

interface Props {
	data: Invitations;
	gallery: IGallery[];
	colorMatrix: TemplateColorObject;
	canEdit?: boolean;
}

const Template = forwardRef(({ data, gallery, colorMatrix, canEdit = false }: Props, ref) => {
	const commentsSectionRef = useRef<HTMLElement>(null);
	const locationSectionRef = useRef<HTMLElement>(null);

	useImperativeHandle(ref, () => ({
		scrollToComments: () =>
			commentsSectionRef.current && commentsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" }),
		scrollToLocation: () =>
			locationSectionRef.current && locationSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
	}));

	const isTemplate = data?.type === INVITATION_TYPES.TEMPLATE;
	const invitationId = data?._id || "";
	const lang = data?.language === LANG_EN ? langEN : langAR;
	const fieldMap = getFieldMap(invitationId);

	const invitation_type_title = useMemo(() => {
		switch (data?.occasion) {
			case INVITATION_OCCASION.ENGAGEMENT:
				return lang.the_engagement_of;
			case INVITATION_OCCASION.KATB_KTAB:
				return lang.the_katb_ktab_of;
			default:
				return lang.the_wedding_of;
		}
	}, [data]);

	const invitation_getting_text = useMemo(() => {
		switch (data?.occasion) {
			case INVITATION_OCCASION.ENGAGEMENT:
				return lang.GETTING_ENGAGED;
			case INVITATION_OCCASION.KATB_KTAB:
				return lang.GETTING_KATB_KTAB;
			default:
				return lang.GETTING_MARRIED;
		}
	}, [data]);

	const invitation_location_header = useMemo(() => {
		switch (data?.occasion) {
			case INVITATION_OCCASION.ENGAGEMENT:
				return lang.engagement_location;
			case INVITATION_OCCASION.KATB_KTAB:
				return lang.katb_ktab_location;
			default:
				return lang.wedding_location;
		}
	}, [data]);

	return (
		<TemplateWrapper id={"template_wrapper"} colormatrix={colorMatrix}>
			<div className="invitation-box">
				{/* SECTION 01 — Royal Hero */}
				<div className="section_01">
					<div className="s01-crown-ornament">
						<span className="ornament-line" />
						<span className="ornament-diamond">◆</span>
						<span className="ornament-line" />
					</div>
					<p className="wedding_of" style={{ fontSize: data.language === LANG_AR ? "25px" : "" }}>
						{invitation_type_title}
					</p>
					<img
						src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ycAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACH5BAEAAAAALAAAAAABAAEAAAICTAEAOw=="
						alt=""
						style={{ display: "none" }}
						id={data?.fieldVisibility?.main_image?.hidden ? "main_image" : ""}
					/>
					{data?.has_images && data?.main_image && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
						<div className="s01-main-image-frame">
							<img
								id="main_image"
								src={data?.main_image}
								data-dispatch-id={fieldMap.MAIN_IMAGE}
								alt="couple"
								className={`s01-main-image ${data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}`}
							/>
							<div className="s01-image-overlay" />
						</div>
					)}
					<div className="s01-names-block">
						<p className="groom_name guide_edit_field" data-dispatch-id={fieldMap.GROOM_NAME}>
							{data?.groom_name || "Groom"}
						</p>
						<div className="s01-names-divider">
							<span>&amp;</span>
						</div>
						<p className="bride_name" data-dispatch-id={fieldMap.BRIDE_NAME}>
							{data?.bride_name || "Bride"}
						</p>
					</div>
					<div className="s01-date-band">
						<span className="ornament-thin-line" />
						<p
							className="date"
							data-dispatch-id={fieldMap.WEDDING_DATE}
							style={{ fontSize: data?.language === LANG_AR ? "20px" : "" }}
						>
							{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate) ||
								"May 4, 2026, 12:00 AM"}
						</p>
						<span className="ornament-thin-line" />
					</div>
					{(canEdit || !data?.fieldVisibility?.save_the_date_text?.hidden) && (
						<p
							className={`save_date_text animate-on-scroll delay-1 ${data?.fieldVisibility?.save_the_date_text?.hidden ? "shaded" : ""}`}
							data-dispatch-id={fieldMap.SAVE_THE_DATE}
						>
							{data?.save_the_date_text}
						</p>
					)}
					<div className="s01-save-btn">
						<SaveTheDateBtn canEdit={canEdit} data={data!} btnText={lang.save_the_date_button_text} />
					</div>
				</div>

				{/* SECTION 02 — Countdown */}
				<div className="section_02">
					<div className="s02-inner">
						<CountDown
							data={data || ({} as Invitations)}
							daysString={lang.days}
							hoursString={lang.hours}
							minutesString={lang.mins}
							secondsString={lang.sec}
						/>
					</div>
				</div>

				{/* SECTION 03 — Partners */}
				<div className="section_03">
					{data?.is_islamic && (
						<p className="s03-islamic-dua">
							بَارَكَ اللهُ لَكُما، وَبَارَكَ عَلَيْكُما، وَجَمَعَ بَيْنَكُما فِي خَيْرٍ
						</p>
					)}
					<div className="s03-top-ornament">
						<span className="ornament-line" />
						<span className="ornament-diamond">◆</span>
						<span className="ornament-line" />
					</div>
					{(canEdit || !data?.fieldVisibility?.intro_text?.hidden) && (
						<p
							className={`s03-intro animate-on-scroll delay-1 ${data?.fieldVisibility?.intro_text?.hidden ? "shaded" : ""}`}
							data-dispatch-id={fieldMap.INTRO_TEXT}
						>
							{data?.intro_text}
						</p>
					)}
					{(canEdit || !data?.fieldVisibility?.sub_intro_text?.hidden) && (
						<p
							className={`s03-sub-intro animate-on-scroll delay-2 ${data?.fieldVisibility?.sub_intro_text?.hidden ? "shaded" : ""}`}
							data-dispatch-id={fieldMap.SUB_INTRO}
						>
							{data?.sub_intro_text}
						</p>
					)}
					<div className="s03-partners">
						<div className="s03-partner-card">
							{data?.has_images && (canEdit || !data?.fieldVisibility?.groom_image?.hidden) && (
								<div className="s03-partner-img-frame">
									<img
										src={data?.groom_image}
										data-dispatch-id={fieldMap.GROOM_IMAGE}
										alt="groom"
										className={`s03-partner-img ${data?.fieldVisibility?.groom_image?.hidden ? "shaded" : ""}`}
									/>
								</div>
							)}
							<p className="s03-partner-name animate-on-scroll delay-1" data-dispatch-id={fieldMap.GROOM_NAME}>
								{data?.groom_name || "Groom"}
							</p>
							<PartnersSocialList
								socials={data?.partner_socials_1 || []}
								canEdit={canEdit}
								dispatchId={fieldMap.SOCIAL_MEDIA_1}
							/>
						</div>
						<div className="s03-divider-vertical">
							<span>♥</span>
						</div>
						<div className="s03-partner-card">
							{data?.has_images && (canEdit || !data?.fieldVisibility?.bride_image?.hidden) && (
								<div className="s03-partner-img-frame">
									<img
										src={data?.bride_image}
										data-dispatch-id={fieldMap.BRIDE_IMAGE}
										alt="bride"
										className={`s03-partner-img ${data?.fieldVisibility?.bride_image?.hidden ? "shaded" : ""}`}
									/>
								</div>
							)}
							<p className="s03-partner-name animate-on-scroll delay-2" data-dispatch-id={fieldMap.BRIDE_NAME}>
								{data?.bride_name || "Bride"}
							</p>
							<PartnersSocialList
								socials={data?.partner_socials_2 || []}
								canEdit={canEdit}
								dispatchId={fieldMap.SOCIAL_MEDIA_2}
							/>
						</div>
					</div>
				</div>

				{/* SECTION 04 — Quran */}
				{data?.is_islamic && (
					<div className="section_04">
						<div className="s04-quran-wrap">
							<p className="s04-bismillah">بِسْمِ اللهِ الرَّحْمَنِ الرَّحِيم</p>
							<p className="s04-aya">
								﴿ وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُم مِّنْ أَنفُسِكُمْ أَزْوَاجًا لِّتَسْكُنُوا إِلَيْهَا وَجَعَلَ
								بَيْنَكُم مَّوَدَّةً وَرَحْمَةً ۚ إِنَّ فِي ذَٰلِكَ لَآيَاتٍ لِّقَوْمٍ يَتَفَكَّرُونَ ﴾
							</p>
							<p className="s04-sadaqa">صَدَقَ اللهُ العَظِيم</p>
						</div>
					</div>
				)}

				{/* SECTION 05 — Love Story */}
				{!data?.fieldVisibility?.loveStory?.hidden && (
					<div className="section_05">
						<div className="s05-header">
							<span className="ornament-line" />
							<span className="ornament-diamond">◆</span>
							<span className="ornament-line" />
							<p className="s05-title">{lang.our_love_story}</p>
						</div>
						{data?.has_images && (canEdit || !data?.fieldVisibility?.story_image?.hidden) && (
							<div className="s05-story-img-wrap">
								<img
									src={data?.story_image}
									data-dispatch-id={fieldMap.STORY_IMAGE}
									alt="story"
									className={`s05-story-img ${data?.fieldVisibility?.story_image?.hidden ? "shaded" : ""}`}
								/>
							</div>
						)}
						{data?.story?.length > 0 && (
							<div
								className={`s05-timeline ${data?.language === LANG_AR ? RTL : ""}`}
								data-dispatch-id={fieldMap.STORY}
							>
								{data.story.map((story, index) => (
									<div key={story._id} className={`s05-story-item animate-on-scroll delay-${index + 1}`}>
										<div className="s05-story-dot" />
										<div className="s05-story-content">
											<p className={`s05-story-title ${data?.language === LANG_AR ? RTL : ""}`}>{story.title}</p>
											<p className={`s05-story-desc ${data?.language === LANG_AR ? RTL : ""}`}>{story.description}</p>
										</div>
									</div>
								))}
							</div>
						)}
						{canEdit && (data?.story || []).length === 0 && (
							<div>
								<div data-dispatch-id={fieldMap.STORY}>
									<div className="add_story">
										<AddIcon />
									</div>
								</div>
							</div>
						)}
					</div>
				)}

				{/* SECTION 06 — Gallery */}
				{!data?.fieldVisibility?.gallery?.hidden && data?.has_images && (
					<div className="section_06">
						<div className="s06-header">
							<span className="ornament-line" />
							<span className="ornament-diamond">◆</span>
							<span className="ornament-line" />
							<p className="s06-title">{lang.our_gallery}</p>
						</div>
						<div data-dispatch-id={fieldMap.GALLERY}>
							<InvitationGallery gallery={gallery || []} canEdit={canEdit} />
						</div>
					</div>
				)}

				{/* SECTION 07 — Comments */}
				{!data?.fieldVisibility?.comments?.hidden && (
					<div className="section_07" ref={commentsSectionRef as React.RefObject<HTMLDivElement>}>
						<div className="s07-header">
							<span className="ornament-line" />
							<span className="ornament-diamond">◆</span>
							<span className="ornament-line" />
							<p className="s07-title">{lang.comments || "Comments"}</p>
						</div>
						{(canEdit || !data?.fieldVisibility?.comments_text?.hidden) && (
							<p
								className={`s07-comments-text animate-on-scroll delay-1 ${data?.fieldVisibility?.comments_text?.hidden ? "shaded" : ""}`}
								data-dispatch-id={fieldMap.COMMENTS_TEXT}
							>
								{data?.comments_text}
							</p>
						)}
						{(canEdit || !data?.fieldVisibility?.comments_sub_text?.hidden) && (
							<p
								className={`s07-comments-sub animate-on-scroll delay-2 ${data?.fieldVisibility?.comments_sub_text?.hidden ? "shaded" : ""}`}
								data-dispatch-id={fieldMap.COMMENTS_SUB_TEXT}
							>
								{data?.comments_sub_text}
							</p>
						)}
						<Comments canEdit={canEdit} language={data?.language as LangType} colormatrix={colorMatrix} />
					</div>
				)}

				{/* SECTION 08 — Location */}
				{!data?.fieldVisibility?.location?.hidden && (
					<div className="section_08" ref={locationSectionRef as React.RefObject<HTMLDivElement>}>
						<div className="s08-header">
							<span className="ornament-line" />
							<span className="ornament-diamond">◆</span>
							<span className="ornament-line" />
							<p className="s08-title">{invitation_location_header}</p>
						</div>
						{(canEdit || !data?.fieldVisibility?.wedding_location_text?.hidden) && (
							<p
								className={`s08-location-text ${data?.fieldVisibility?.wedding_location_text?.hidden ? "shaded" : ""}`}
								data-dispatch-id={fieldMap.LOCATION_TEXT}
							>
								{data?.wedding_location_text}
							</p>
						)}
						<div className="s08-map-wrap" data-dispatch-id={fieldMap.MAP}>
							<MapComponent canEdit={canEdit} />
						</div>
						<div className="s08-map-btn" data-dispatch-id={fieldMap.MAP}>
							<MapButton canEdit={canEdit} text={lang.location} />
						</div>
						{data?.has_images && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
							<div
								className={`s08-conclusion-img-wrap ${data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}`}
								data-dispatch-id={fieldMap.MAIN_IMAGE}
							>
								<img src={data?.main_image} alt="couple" className="s08-conclusion-img" />
								<div className="s08-conclusion-overlay" />
								<div className={`s08-names-float ${data?.language === LANG_AR ? RTL : ""} animate-on-scroll delay-1`}>
									<span data-dispatch-id={fieldMap.GROOM_NAME}>{data?.groom_name}</span>
									<span className="s08-and">{lang.and}</span>
									<span data-dispatch-id={fieldMap.BRIDE_NAME}>{data?.bride_name}</span>
								</div>
							</div>
						)}
						{(canEdit || !data?.fieldVisibility?.conclusion_text?.hidden) && (
							<div>
								<p
									className={`s08-conclusion-text animate-on-scroll delay-2 ${data?.fieldVisibility?.conclusion_text?.hidden ? "shaded" : ""}`}
									data-dispatch-id={fieldMap.CONCLUSION_TEXT}
								>
									{data?.conclusion_text}
								</p>
							</div>
						)}
					</div>
				)}

				<Footer colormatrix={colorMatrix} />
			</div>
		</TemplateWrapper>
	);
});

export default Template;
